import React, { useEffect, useState } from "react"
import {
	Alert, Box, Dialog, DialogContent, DialogTitle,
	Divider, IconButton, MenuItem, Select, Table, TableBody,
	TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material"
import { IconSeeker, NumberFieldSeeker } from "@ui-seeker/core"
import { AutocompleteSeeker } from "@ui-seeker/core"
import { getSubmit } from "@ui-seeker/utils"
import { BACKEND_APIS } from "@constants"
import { durationErrors, isValidDurationRange } from "Pages/AutoEuropeTool/Page/@utils/durations"

const UNDERCUT_TYPES = {
	0: { label: "€ Currency" },
	1: { label: "% Percentage" },
}

const UNDERCUT_DIRECTIONS = {
	0: { label: "▼ Down" },
	1: { label: "▲ Up" },
}

const ALL_PROVIDERS_OPTION = { value: 0, label: "All Providers" }

const ALL_PROVIDERS_DURATION_HINT = "“All Providers” requires a duration range: the rule's general value already applies to every provider across all durations, so an “All Providers” override only makes sense for a specific duration."

const OVERLAP_HINT = "That duration range overlaps another override for the same provider. Ranges of one provider cannot overlap — edit or remove the existing one first."

// Un conflicto sin duracion informada no es un solape: es que ese provider ya tiene
// el override general. Merece su propio texto o el aviso desorienta.
const GENERAL_DUPLICATE_HINT = "That provider already has an override without a duration range. Set a duration range, or edit/remove the existing one first."


export default function ProviderOverrideModal({ open, onClose, title, overrides, onChange, isUndercut, withDurations = false, readOnly = false }) {
	// Duration exceptions are shared by Undercut and any feature opting in via `withDurations`
	// (e.g. Minimum Profit). Type/Direction columns remain exclusive to Undercut (`isUndercut`).
	const showDurations = isUndercut || withDurations
	const [newProvider, setNewProvider] = useState(null)
	const [newValue, setNewValue] = useState('')
	const [newType, setNewType] = useState(1)
	const [newDirection, setNewDirection] = useState(0)
	const [newDurationFrom, setNewDurationFrom] = useState('')
	const [newDurationTo, setNewDurationTo] = useState('')
	const [allProviderOptions, setAllProviderOptions] = useState([])
	const [pendingWarning, setPendingWarning] = useState(false)
	const [editWarning, setEditWarning] = useState(false)
	const [editingIndex, setEditingIndex] = useState(null)
	const [editValue, setEditValue] = useState('')
	const [editType, setEditType] = useState(1)
	const [editDirection, setEditDirection] = useState(0)
	const [editDurationFrom, setEditDurationFrom] = useState('')
	const [editDurationTo, setEditDurationTo] = useState('')

	useEffect(() => {
		if (!open || allProviderOptions.length > 0) return
		getSubmit(`${BACKEND_APIS.MASTER_DATA}/options/provider`)
			.then(options => setAllProviderOptions(options ?? []))
	}, [open])

	const usedProviderIds = overrides.map(o => o.providerId)
	const filteredOptions = [
		...(showDurations ? [ALL_PROVIDERS_OPTION] : []),
		...(showDurations ? allProviderOptions : allProviderOptions.filter(o => !usedProviderIds.includes(o.value))),
	]

	const isAllProviders = newProvider === ALL_PROVIDERS_OPTION.value
	const editingItemIsAllProviders = editingIndex !== null && overrides[editingIndex]?.providerId === ALL_PROVIDERS_OPTION.value

	const normDur = v => v !== '' ? Number(v) : null
	const durationsConflict = (o, from, to) => {
		const oFrom = o.duration?.durationFrom ?? null
		const oTo = o.duration?.durationTo ?? null
		const nFrom = normDur(from)
		const nTo = normDur(to)
		if (oFrom === null && nFrom === null) return true
		if (oFrom === null || nFrom === null) return false
		return oFrom <= nTo && nFrom <= oTo
	}

	const isDuplicate = showDurations && newProvider !== null &&
		overrides.some(o => o.providerId === newProvider && durationsConflict(o, newDurationFrom, newDurationTo))

	const editIsDuplicate = showDurations && editingIndex !== null && overrides.some((o, i) =>
		i !== editingIndex &&
		o.providerId === overrides[editingIndex].providerId &&
		durationsConflict(o, editDurationFrom, editDurationTo)
	)

	const sortedOverrides = overrides
		.map((item, originalIndex) => ({ ...item, originalIndex }))
		.sort((a, b) => {
			const nameA = (a.providerId === ALL_PROVIDERS_OPTION.value ? ALL_PROVIDERS_OPTION.label : (a.providerName || '')).toLowerCase()
			const nameB = (b.providerId === ALL_PROVIDERS_OPTION.value ? ALL_PROVIDERS_OPTION.label : (b.providerName || '')).toLowerCase()
			const nameCmp = nameA.localeCompare(nameB)
			if (nameCmp !== 0) return nameCmp
			if (!showDurations) return 0
			const aHas = a.duration?.durationFrom != null
			const bHas = b.duration?.durationFrom != null
			if (aHas !== bHas) return aHas ? 1 : -1
			if (!aHas) return 0
			const fromCmp = a.duration.durationFrom - b.duration.durationFrom
			return fromCmp !== 0 ? fromCmp : a.duration.durationTo - b.duration.durationTo
		})

	const canAdd = newProvider !== null && newValue !== '' && !isNaN(Number(newValue)) &&
		(!showDurations || isValidDurationRange(newDurationFrom, newDurationTo, isAllProviders)) &&
		!isDuplicate

	const canConfirmEdit = editValue !== '' && !isNaN(Number(editValue)) &&
		(!showDurations || isValidDurationRange(editDurationFrom, editDurationTo, editingItemIsAllProviders)) &&
		!editIsDuplicate

	const allProvidersNeedsDuration = showDurations && isAllProviders && !isValidDurationRange(newDurationFrom, newDurationTo, true)
	const editAllProvidersNeedsDuration = showDurations && editingItemIsAllProviders && !isValidDurationRange(editDurationFrom, editDurationTo, true)

	// Sin esto el + se quedaba deshabilitado sin decir por qué: los rangos con
	// decimales, fuera de 1–180 o solapados son la causa más habitual.
	const newDurErrors = showDurations ? durationErrors(newDurationFrom, newDurationTo) : {}
	const editDurErrors = showDurations ? durationErrors(editDurationFrom, editDurationTo) : {}

	// durationsConflict solo puede dispararse por dos motivos: dos generales, o dos
	// rangos que se pisan. Sin duracion informada es el primero.
	const addDuplicateHint = normDur(newDurationFrom) === null ? GENERAL_DUPLICATE_HINT : OVERLAP_HINT
	const editDuplicateHint = normDur(editDurationFrom) === null ? GENERAL_DUPLICATE_HINT : OVERLAP_HINT

	const handleAdd = () => {
		if (!canAdd) return
		const selectedOption = newProvider === ALL_PROVIDERS_OPTION.value
			? ALL_PROVIDERS_OPTION
			: allProviderOptions.find(o => o.value === newProvider)
		const item = {
			providerId: newProvider,
			providerName: selectedOption?.label ?? `Provider #${newProvider}`,
			value: Number(newValue),
			...(isUndercut && { type: newType }),
			...(isUndercut && { direction: newDirection }),
			...(showDurations && newDurationFrom !== '' && newDurationTo !== '' && {
				duration: { durationFrom: Number(newDurationFrom), durationTo: Number(newDurationTo) }
			}),
		}
		onChange([...overrides, item])
		setNewProvider(null)
		setNewValue('')
		setNewType(1)
		setNewDirection(0)
		setNewDurationFrom('')
		setNewDurationTo('')
		setPendingWarning(false)
	}

	const handleClose = () => {
		if (editingIndex !== null) { setEditWarning(true); return }
		if (canAdd) { setPendingWarning(true); return }
		setPendingWarning(false)
		setEditWarning(false)
		setEditingIndex(null)
		onClose()
	}

	const handleDelete = (index) => {
		if (editingIndex === index) setEditingIndex(null)
		onChange(overrides.filter((_, i) => i !== index))
	}

	const handleClearAll = () => {
		setEditingIndex(null)
		onChange([])
	}

	const startEdit = (index) => {
		setEditingIndex(index)
		setEditValue(String(overrides[index].value))
		setEditType(overrides[index].type ?? 1)
		setEditDirection(overrides[index].direction ?? 0)
		setEditDurationFrom(overrides[index].duration?.durationFrom != null ? String(overrides[index].duration.durationFrom) : '')
		setEditDurationTo(overrides[index].duration?.durationTo != null ? String(overrides[index].duration.durationTo) : '')
	}

	const confirmEdit = (index) => {
		if (editValue === '' || isNaN(Number(editValue))) return
		const itemIsAllProviders = overrides[index]?.providerId === ALL_PROVIDERS_OPTION.value
		if (showDurations && !isValidDurationRange(editDurationFrom, editDurationTo, itemIsAllProviders)) return
		if (editIsDuplicate) return
		const updated = overrides.map((item, i) => i === index
			? {
				...item,
				value: Number(editValue),
				...(isUndercut && { type: editType }),
				...(isUndercut && { direction: editDirection }),
				...(showDurations && {
					duration: editDurationFrom !== '' && editDurationTo !== ''
						? { durationFrom: Number(editDurationFrom), durationTo: Number(editDurationTo) }
						: null
				}),
			}
			: item
		)
		onChange(updated)
		setEditingIndex(null)
		setEditWarning(false)
	}

	const cancelEdit = () => { setEditingIndex(null); setEditWarning(false) }

	return (
		<Dialog open={open} onClose={handleClose} maxWidth={showDurations ? "md" : "sm"} fullWidth>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				{overrides.length > 0 ? (
					<>
						{!readOnly && (
							<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
								<Tooltip title="Remove all overrides">
									<IconButton size="small" onClick={handleClearAll} color="error">
										<IconSeeker icon="xmark" fontSize="small"/>
									</IconButton>
								</Tooltip>
							</Box>
						)}
						{editWarning && (
							<Alert severity="warning" sx={{ mb: 1.5 }}>
								You have unsaved changes. Click <strong>✓</strong> to save or <strong>✕</strong> to discard before closing.
							</Alert>
						)}
						{editAllProvidersNeedsDuration && (
							<Alert severity="info" sx={{ mb: 1.5 }}>
								{ALL_PROVIDERS_DURATION_HINT}
							</Alert>
						)}
						{editIsDuplicate && (
							<Alert severity="error" sx={{ mb: 1.5 }}>
								{editDuplicateHint}
							</Alert>
						)}
						<Table size="small" sx={{ mb: 2 }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ color: 'text.primary' }}>Provider</TableCell>
									{showDurations && <TableCell sx={{ color: 'text.primary' }}>Dur. From</TableCell>}
									{showDurations && <TableCell sx={{ color: 'text.primary' }}>Dur. To</TableCell>}
									<TableCell sx={{ color: 'text.primary' }}>Value</TableCell>
									{isUndercut && <TableCell sx={{ color: 'text.primary' }}>Type</TableCell>}
									{isUndercut && <TableCell sx={{ color: 'text.primary' }}>Direction</TableCell>}
									{!readOnly && <TableCell/>}
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedOverrides.map((item, i) => (
									<TableRow key={i}>
										<TableCell>{item.providerId === ALL_PROVIDERS_OPTION.value ? ALL_PROVIDERS_OPTION.label : (item.providerName || `Provider #${item.providerId}`)}</TableCell>
										{showDurations && (
											<TableCell>
												{editingIndex === item.originalIndex ? (
													<NumberFieldSeeker
														placeholder="—"
														value={editDurationFrom}
														onChange={e => setEditDurationFrom(e.target.value)}
														inputProps={{ step: 1 }}
														error={editDurErrors.from}
														sx={{ width: 72 }}
													/>
												) : (item.duration?.durationFrom ?? '—')}
											</TableCell>
										)}
										{showDurations && (
											<TableCell>
												{editingIndex === item.originalIndex ? (
													<NumberFieldSeeker
														placeholder="—"
														value={editDurationTo}
														onChange={e => setEditDurationTo(e.target.value)}
														inputProps={{ step: 1 }}
														error={editDurErrors.to}
														sx={{ width: 72 }}
													/>
												) : (item.duration?.durationTo ?? '—')}
											</TableCell>
										)}
										<TableCell>
											{editingIndex === item.originalIndex ? (
												<NumberFieldSeeker
													placeholder="0"
													value={editValue}
													onChange={e => setEditValue(e.target.value)}
													sx={{ width: 80 }}
												/>
											) : item.value}
										</TableCell>
										{isUndercut && (
											<TableCell>
												{editingIndex === item.originalIndex ? (
													<Select
														size="small"
														value={editType}
														onChange={e => setEditType(e.target.value)}
													>
														<MenuItem value={0}>€ Currency</MenuItem>
														<MenuItem value={1}>% Percentage</MenuItem>
													</Select>
												) : UNDERCUT_TYPES[item.type]?.label ?? item.type}
											</TableCell>
										)}
										{isUndercut && (
											<TableCell>
												{editingIndex === item.originalIndex ? (
													<Select
														size="small"
														value={editDirection}
														onChange={e => setEditDirection(e.target.value)}
													>
														<MenuItem value={0}>▼ Down</MenuItem>
														<MenuItem value={1}>▲ Up</MenuItem>
													</Select>
												) : UNDERCUT_DIRECTIONS[item.direction ?? 0]?.label}
											</TableCell>
										)}
										{!readOnly && (
											<TableCell padding="none">
												<Box sx={{ display: 'flex' }}>
													{editingIndex === item.originalIndex ? (
														<>
															<IconButton size="small" onClick={() => confirmEdit(item.originalIndex)} disabled={!canConfirmEdit} color="primary">
																<IconSeeker icon="check" fontSize="small"/>
															</IconButton>
															<IconButton size="small" onClick={cancelEdit}>
																<IconSeeker icon="xmark" fontSize="small"/>
															</IconButton>
														</>
													) : (
														<>
															<IconButton size="small" onClick={() => startEdit(item.originalIndex)}>
																<IconSeeker icon="pen" fontSize="small"/>
															</IconButton>
															<IconButton size="small" onClick={() => handleDelete(item.originalIndex)}>
																<IconSeeker icon="xmark" fontSize="small"/>
															</IconButton>
														</>
													)}
												</Box>
											</TableCell>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</>
				) : (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						No provider overrides yet.
					</Typography>
				)}

				{!readOnly && <>
					<Divider sx={{ mb: 2 }}/>
					<Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>Add override</Typography>

					{pendingWarning && (
						<Alert severity="warning" sx={{ mb: 1.5 }}>
							You have a pending override. Click <strong>+</strong> to add it or clear the fields before closing.
						</Alert>
					)}

					{allProvidersNeedsDuration && (
						<Alert severity="info" sx={{ mb: 1.5 }}>
							{ALL_PROVIDERS_DURATION_HINT}
						</Alert>
					)}

					{isDuplicate && (
						<Alert severity="error" sx={{ mb: 1.5 }}>
							{addDuplicateHint}
						</Alert>
					)}

					<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
						<Box sx={{ flex: 2 }}>
							<AutocompleteSeeker
								filterSelectedOptions={false}
								disableClearable={false}
								label="Provider"
								placeholder="Select provider"
								options={filteredOptions}
								value={newProvider}
								onChange={e => { setNewProvider(e.target.value); setPendingWarning(false) }}
							/>
						</Box>
						{showDurations && (
							<Box sx={{ flex: 1 }}>
								<NumberFieldSeeker
									label="Dur. From"
									placeholder="—"
									value={newDurationFrom}
									onChange={e => { setNewDurationFrom(e.target.value); setPendingWarning(false) }}
									inputProps={{ step: 1 }}
									error={newDurErrors.from}
								/>
							</Box>
						)}
						{showDurations && (
							<Box sx={{ flex: 1 }}>
								<NumberFieldSeeker
									label="Dur. To"
									placeholder="—"
									value={newDurationTo}
									onChange={e => { setNewDurationTo(e.target.value); setPendingWarning(false) }}
									inputProps={{ step: 1 }}
									error={newDurErrors.to}
								/>
							</Box>
						)}
						<Box sx={{ flex: 1 }}>
							<NumberFieldSeeker
								label="Value"
								placeholder="0"
								value={newValue}
								onChange={e => { setNewValue(e.target.value); setPendingWarning(false) }}
							/>
						</Box>
						{isUndercut && (
							<Box sx={{ flex: 1 }}>
								<Select
									size="small"
									fullWidth
									value={newType}
									onChange={e => setNewType(e.target.value)}
									sx={{ mt: 0.25 }}
								>
									<MenuItem value={0}>€ Currency</MenuItem>
									<MenuItem value={1}>% Percentage</MenuItem>
								</Select>
							</Box>
						)}
						{isUndercut && (
							<Box sx={{ flex: 1 }}>
								<Select
									size="small"
									fullWidth
									value={newDirection}
									onChange={e => setNewDirection(e.target.value)}
									sx={{ mt: 0.25 }}
								>
									<MenuItem value={0}>▼ Down</MenuItem>
									<MenuItem value={1}>▲ Up</MenuItem>
								</Select>
							</Box>
						)}
						<Box sx={{ pt: 0.5 }}>
							<IconButton onClick={handleAdd} disabled={!canAdd} color="primary">
								<IconSeeker icon="plus"/>
							</IconButton>
						</Box>
					</Box>
				</>}
			</DialogContent>
		</Dialog>
	)
}
